import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type Patient from "parog-web/models/patient";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type {
  FindDocumentsCriteria,
  INS,
  InsIdentityDocumentType
} from "parog-web/cnam/types";
import {
  DmpAuthorizationStatus,
  DocumentStatus,
  DocumentVisibility,
  InsStatus
} from "parog-web/cnam/types";
import InsDocuments from "parog-web/components/cnam-services-access-form/ins-documents";
import RateExceeded from "parog-web/components/cnam-services-access-form/rate-exceeded";
import AuthorizationState from "parog-web/components/dmp-retrieve-access-form/authorization-state";
import SetSecretConnection from "parog-web/components/dmp-retrieve-access-form/set-secret-connection";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import { CnamApiCpsNoPracticeLocations } from "parog-web/utils/cnam-api-errors";
import type { AuthenticationContext } from "../authentication-context";
import InsWithConfirmationRetrievalContext from "../ins-with-confirmation-retrieval-context";
import { State } from "./cnam-context";

export default abstract class DmpContext extends InsWithConfirmationRetrievalContext {
  declare authenticationContext: AuthenticationContext;
  declare dmpAdapter: DmpAdapter;
  declare insiAdapter: InsiAdapter;

  @tracked medicDmpAuthorizationStatus?: string;
  @tracked shouldAuthorizeMedicToAccessPatientDmp = false;

  @tracked declare secretConnection?: boolean;

  @tracked connectorCreated = false;
  @tracked practiceLocationsChecked = false;

  @tracked declare insDocumentIsQualifying: boolean;
  @tracked declare insIdentityDocument: InsIdentityDocumentType | undefined;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    store: Store,
    requestManager,
    onError?: () => void
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      insiAdapter,
      dmpAdapter,
      store,
      requestManager,
      "dmp"
    );

    this.patient = patient;
    this.authenticationContext = authenticationContext;
    this.dmpAdapter = dmpAdapter;
    this.insiAdapter = insiAdapter;
    this.store = store;
    this.onError = onError;

    this.insIdentityDocument = patient.insIdentityDocument;
    this.insDocumentIsQualifying = patient.insDocumentIsQualifying;

    this.authenticationContext.forcedIdentifier =
      this.authenticationContext.medic.forceFinessForCnamRetrieval;

    this.insProcessNeeded = false;

    this.states = [
      ...this.states,
      new State(
        "check-practice-locations",
        async (): Promise<boolean> => {
          return Promise.resolve(!this.practiceLocationsChecked);
        },
        async (): Promise<void> => {
          if (
            this.authenticationContext?.availableLocations?.length == 0 &&
            !this.authenticationContext?.medic.forceFinessForCnamRetrieval
          ) {
            throw new CnamApiCpsNoPracticeLocations();
          }
          this.practiceLocationsChecked = true;
        }
      ),
      new State(
        "set-secret-connection",
        async (): Promise<boolean> => {
          return Promise.resolve(
            (await this.dmpAdapter.shouldSetSecretConnection(this.patient)) &&
              this.secretConnection == undefined
          );
        },
        async (): Promise<void> => {
          return;
        },
        SetSecretConnection
      ),
      new State(
        "ins-qualification-needed",
        async (): Promise<boolean> => {
          return Promise.resolve(
            this.patient.insStatus !== InsStatus.Qualified &&
              !this.insProcessNeeded
          );
        },
        async (): Promise<void> => {
          this.patient.insIdentityDocument = this.insIdentityDocument;
          this.patient.insDocumentIsQualifying = this.insDocumentIsQualifying!;
          await this.patient.save();
          this.insProcessNeeded = true;
        },
        InsDocuments
      ),
      new State(
        "rate-exceeded",
        (): Promise<boolean> => {
          return Promise.resolve(
            (this.dmpAdapter.hourlyRateExceeded ||
              this.dmpAdapter.dailyRateExceeded) &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          return;
        },
        RateExceeded
      ),
      new State(
        "create-connector",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.connectorCreated &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.dmpAdapter.createConnector();
          this.connectorCreated = true;
          const medicDmpAuthorizationStatus = await this.dmpAdapter.td02Exist(
            this.patient.ins!,
            this.secretConnection!
          );
          this.shouldAuthorizeMedicToAccessPatientDmp =
            medicDmpAuthorizationStatus?.authorizationStatus !==
            DmpAuthorizationStatus.Authorized;
        }
      ),
      new State(
        "authorize-medic",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.shouldAuthorizeMedicToAccessPatientDmp &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.dmpAdapter.td03AddAuthorization(
            this.patient.ins!,
            this.secretConnection!
          );

          const medicDmpAuthorizationStatus = await this.dmpAdapter.td02Exist(
            this.patient.ins!,
            this.secretConnection!
          );
          this.shouldAuthorizeMedicToAccessPatientDmp =
            medicDmpAuthorizationStatus?.authorizationStatus !==
            DmpAuthorizationStatus.Authorized;
        },
        AuthorizationState
      )
    ];
  }

  reset() {
    super.reset();
    this.medicDmpAuthorizationStatus = undefined;
    this.shouldAuthorizeMedicToAccessPatientDmp = false;
    this.insIdentityDocument = this.patient.insIdentityDocument;
    this.insDocumentIsQualifying = this.patient.insDocumentIsQualifying;
    this.secretConnection = undefined;
    this.currentState = undefined;
    this.connectorCreated = false;
    this.practiceLocationsChecked = false;
  }

  async findDocuments(
    patient: Patient,
    store: Store,
    filterCriteria: FindDocumentsCriteria = {}
  ): Promise<Array<DmpDocumentMetadata>> {
    const criteria: FindDocumentsCriteria = Object.assign(
      {
        visibilities: [DocumentVisibility.NORMAL],
        statuses: [DocumentStatus.APPROVED]
      },
      filterCriteria
    );

    const documents = await this.dmpAdapter.td31Find(
      patient.ins as INS,
      criteria,
      this.secretConnection!
    );

    await store
      .createRecord("dmp-retrieval-started-journal-event", {
        cpsId: this.authenticationContext.cpsInformations!.medicInternalId,
        occurredAt: new Date(),
        patient,
        cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
      })
      .save();

    return documents;
  }
}
